import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Product } from "../types/product";
import { getAdminProducts } from "../services/productsApi";

// Define context type
interface ProductsContextProps {
  products: Product[];
  setProducts: React.Dispatch<React.SetStateAction<Product[]>>;
  updateProductQuantity: (id: string, newQuantity: number) => void;
  deleteProduct: (id: string) => void;
}

// Create context
const ProductsContext = createContext<ProductsContextProps | null>(null);

export const ProductsProvider = ({ children }: { children: ReactNode }) => {
  const rawProducts: Omit<Product, "status">[] = [
    {
      id: "1",
      name: "Herbal Repair Shampoo",
      brand: "LuxeCare",
      category: "Hair Care",
      price: 24.99,
      stock: 15,
      image: "/products/Shampoo.jpg",
      quantity: 1,
    },
    {
      id: "2",
      name: "Silky Smooth Conditioner",
      brand: "LuxeCare",
      category: "Hair Care",
      price: 19.99,
      stock: 8,
      image: "/products/Conditioner.avif",
      quantity: 1,
    },
    {
      id: "3",
      name: "Classic Red Nail Polish",
      brand: "BeautyPro",
      category: "Cosmetics",
      price: 12.99,
      stock: 20,
      image: "/products/nail-polish.webp",
      quantity: 1,
    },
  ];

  const defaultProducts: Product[] = rawProducts.map((p) => {
    let status: Product["status"] = "In Stock";
    const stock = p.stock ?? 0;
    if (stock === 0) status = "Out of Stock";
    else if (stock < 10) status = "Low Stock";
    // Remove quantity from default products - they shouldn't have quantity in catalog
    const { quantity, ...productWithoutQuantity } = p;
    return { ...productWithoutQuantity, stock, status };
  });

  const [products, setProducts] = useState<Product[]>(() => {
    // Check if we're on POS page - if so, always start with empty cart
    const isPosPage = window.location.pathname.includes('/pos') || window.location.pathname === '/pos';

    const saved = localStorage.getItem("products");
    if (saved) {
      try {
        const parsed: Product[] = JSON.parse(saved);
        // Filter out cart items (items with quantity property are likely from POS cart)
        // Cart items should not persist across page refreshes
        const isCartData = Array.isArray(parsed) && parsed.length > 0 &&
          parsed.some((p: any) => p.quantity !== undefined && p.quantity > 0);

        if (isCartData || isPosPage) {
          // This is cart data or we're on POS page, don't load it - return empty array
          // Clear it from localStorage as well
          localStorage.removeItem("products");
          return [];
        }

        // This is product catalog data, load it
        // Auto-fix statuses dynamically on every load
        // Remove quantity property if it exists (it shouldn't be in catalog)
        return parsed.map((p) => {
          let status: Product["status"] = "In Stock";
          if ((p.stock ?? 0) === 0) status = "Out of Stock";
          else if ((p.stock ?? 0) < 10) status = "Low Stock";
          // Remove quantity property from catalog products
          const { quantity, ...productWithoutQuantity } = p;
          return { ...productWithoutQuantity, status };
        });
      } catch (e) {
        // If parsing fails, return empty array if on POS, otherwise default products
        if (isPosPage) {
          return [];
        }
        return defaultProducts;
      }
    }
    // If on POS page, start with empty array, otherwise default products
    if (isPosPage) {
      return [];
    }
    return defaultProducts;
  });

  useEffect(() => {
    let isMounted = true;

    const fetchProducts = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        const serverProducts = await getAdminProducts();
        if (isMounted && serverProducts.length > 0) {
          // Check if we're on POS page - if so, don't load products into cart
          const isPosPage = window.location.pathname.includes('/pos') || window.location.pathname === '/pos';
          if (!isPosPage) {
            // Only set products if not on POS page (for product catalog pages)
            // On POS page, products context is used for cart only, not catalog
            setProducts(serverProducts);
          }
        }
      } catch (error) {
        console.error("Failed to fetch products", error);
      }
    };

    fetchProducts();

    return () => {
      isMounted = false;
    };
  }, []);

  /**
     Sync to localStorage (Persistence)
     * Only persist if it's product catalog data, not cart data
     * Cart items (with quantity) should not be persisted
     */
  useEffect(() => {
    // Check if current products look like cart items
    const isCartData = products.length > 0 &&
      products.some((p) => p.quantity !== undefined && p.quantity > 0);

    // Only persist product catalog data, not cart data
    if (!isCartData && products.length > 0) {
      localStorage.setItem("products", JSON.stringify(products));
    } else if (isCartData) {
      // If it's cart data, don't persist it - remove from localStorage
      // This ensures cart doesn't persist across refreshes
      localStorage.removeItem("products");
    }
  }, [products]);

  /**
    Update product quantity (used in POS)
     */
  const updateProductQuantity = (id: string, newQuantity: number) => {
    setProducts((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, quantity: newQuantity < 0 ? 0 : newQuantity } : p
      )
    );
  };

  /**
   Delete a product
   */
  const deleteProduct = (id: string) => {
    setProducts((prev) => prev.filter((p) => p.id !== id));
  };

  return (
    <ProductsContext.Provider
      value={{ products, setProducts, updateProductQuantity, deleteProduct }}
    >
      {children}
    </ProductsContext.Provider>
  );
};

/**
Hook for easy access
 */
export const useProducts = () => {
  const ctx = useContext(ProductsContext);
  if (!ctx) {
    throw new Error("useProducts must be used within a ProductsProvider");
  }
  return ctx;
};
